import {
  AppIcon,
  ContentNugget,
  lottieAnimations,
  Pagination,
  SearchResult,
  transformSearchResultsToNuggets,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { NuggetCardList } from '@backrs_ui/grow-ui/src/components/NuggetCards/NuggetCardList';

import { Box, SxProps, Theme, TextField, Button, Typography } from '@mui/material';
import Lottie from 'lottie-react';

import { SearchFiltersGroup } from '../components/SearchFiltersGroup';
import { SearchCategory, SearchParentCategory, SearchPageCategory } from '../search.types';

const SEARCH_STRINGS = appStrings.modules.search;

type SearchPageProps = Readonly<{
  categories: SearchParentCategory[];
  searchResults?: SearchResult;
  currentSearchTerm: string;
  selectedItems?: SearchCategory[];
  isSearching: boolean;
  currentPageCategory: SearchPageCategory;
  handlePanelNuggetClick: (nugget: ContentNugget) => void;
  handleNextPage: () => void;
  handlePrevPage: () => void;
  setSelectedItems: (category: SearchCategory[]) => void;
  handleUpdateSearchTerm: (searchTerm: string) => void;
  handlePageChange: (pageNum: number) => void;
  initiateSearch: (items?: SearchCategory[]) => void;
  clearSearch: (clearItems?: boolean) => void;
}>;

export const SearchPage = ({
  categories,
  searchResults,
  currentSearchTerm,
  selectedItems,
  isSearching,
  currentPageCategory,
  handlePanelNuggetClick,
  clearSearch,
  setSelectedItems,
  handlePrevPage,
  handleNextPage,
  handleUpdateSearchTerm,
  handlePageChange,
  initiateSearch,
}: SearchPageProps) => {
  // we combine the competency results and the nuggets into one list to display
  // if we click on a competency, we want to navigate to the competency page
  // Playlists are now transformed into nuggets and included in the main list to preserve API order
  const { nuggets: combinedSearchResultList } = searchResults
    ? transformSearchResultsToNuggets(searchResults.results, true)
    : {
        nuggets: [],
      };

  const handleSearchEnterKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      initiateSearch();
    }
  };

  const totalPages = Math.ceil((searchResults?.total || 1) / (searchResults?.perPage || 1));
  const isPaginated = totalPages > 1;

  // Modified condition to prevent showing "no results" while typing a new search
  const hasSearchedAndFoundNothing =
    !isSearching &&
    searchResults !== undefined &&
    searchResults.total === 0 &&
    (currentSearchTerm.trim() !== '' || (selectedItems || [])?.length > 0);

  return (
    <Box sx={parentContainerStyle}>
      <Box sx={outerContentContainerStyle}>
        <Box sx={searchAndFilterContainerStyle}>
          {/* SEARCH BAR */}
          <Box sx={searchContainerStyle}>
            <TextField
              id="search-input"
              label={SEARCH_STRINGS.searchPageLabels[currentPageCategory]}
              variant="outlined"
              placeholder={SEARCH_STRINGS.searchPagePlaceholders[currentPageCategory]}
              value={currentSearchTerm}
              onChange={(event) => handleUpdateSearchTerm(event.target.value)}
              InputProps={{
                startAdornment: (
                  <AppIcon iconName="SearchMd" color="currentColor" diameter="20px" />
                ),
              }}
              onKeyDown={handleSearchEnterKey}
              sx={searchInputStyle}
            />
            <Box sx={searchButtonContainerStyle}>
              <Button
                id="search-clear-button"
                hierarchy="Secondary gray"
                size="lg"
                onClick={() => clearSearch()}
              >
                {SEARCH_STRINGS.clear}
              </Button>
              <Button
                id="search-submit-button"
                hierarchy="Primary"
                size="lg"
                onClick={() => initiateSearch(selectedItems)}
                disabled={!currentSearchTerm && !selectedItems?.length}
              >
                {SEARCH_STRINGS.searchPlaceholder}
              </Button>
            </Box>
          </Box>

          {/* SEARCH FILTERS */}
          <SearchFiltersGroup
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            categories={categories}
          />
        </Box>

        {/* SEARCH RESULT CONTENT AND LOADING */}

        <Box sx={contentContainerStyle}>
          {!isSearching && (combinedSearchResultList.length > 0 || !currentSearchTerm) && (
            <NuggetCardList
              nuggets={combinedSearchResultList}
              onClick={handlePanelNuggetClick}
              direction="row"
            />
          )}

          {hasSearchedAndFoundNothing && (
            <Box sx={noResultsContainerStyle}>
              <Box component="img" src="/images/no-search-results.svg" />
              <Typography variant="text-lg-regular">{SEARCH_STRINGS.noResults}</Typography>
            </Box>
          )}

          {isSearching && (
            <Box sx={loadingAnimationContainerStyle}>
              <Lottie animationData={lottieAnimations.dotsDotting} loop />
            </Box>
          )}
        </Box>
      </Box>
      {/* PAGINATION */}
      {isPaginated && (
        <Pagination
          currentPage={searchResults?.page || 1}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          handlePrevPage={handlePrevPage}
          handleNextPage={handleNextPage}
          sticky
        />
      )}
    </Box>
  );
};

const parentContainerStyle: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  justifyContent: 'space-between',
  display: 'flex',
  flexDirection: 'column',
};

const outerContentContainerStyle: SxProps<Theme> = {
  width: '100%',
};

const contentContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  paddingBottom: {
    xs: backrs.spacing['spacing-lg'],
    md: backrs.spacing['spacing-2xl'],
  },
  mt: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-2xl'],
  },
});

const searchContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  boxSizing: 'border-box',
  marginBottom: backrs.spacing['spacing-2xl'],
  paddingX: backrs.spacing['spacing-2xl'],
  paddingTop: backrs.spacing['spacing-5xl'],
  paddingBottom: backrs.spacing['spacing-xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  gap: backrs.spacing['spacing-xl'],
  flexWrap: 'wrap',
  '@container (min-width: 600px)': {
    flexWrap: 'nowrap',
  },
});

const searchInputStyle: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    minHeight: '48px',
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  },
  '& .MuiOutlinedInput-root.Mui-focused': {
    border: `1px solid ${backrs.colors['Colors/Border/border-brand']}`,
  },
});

const searchButtonContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  gap: backrs.spacing['spacing-xl'],
  '@container (min-width: 600px)': {
    justifyContent: 'space-between',
  },
});

const loadingAnimationContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  width: '100%',
  pt: backrs.spacing['spacing-5xl'],
});

const searchAndFilterContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  pb: backrs.spacing['spacing-4xl'],
});

const noResultsContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'center',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  whiteSpace: 'pre-line',
  alignSelf: 'center',
  gap: backrs.spacing['spacing-5xl'],

  mt: {
    xs: backrs.spacing['spacing-4xl'],
    sm: backrs.spacing['spacing-8xl'],
  },
  img: {
    width: { xs: '100%', sm: '100%' },
  },
});
